import { GearSix, SignOut, User } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import { Link } from 'react-router';
import { CountBadge } from '../../../shared/components/count-badge';
import { InitialAvatar } from '../../../shared/components/initial-avatar';
import { ROUTES } from '../../../shared/constants/app.constant';
import { useDisclosure } from '../../../shared/hooks/use-disclosure';
import { useLogout, useSession } from '../../auth/hooks/use-session';
import { usePendingJoinRequests } from '../hooks/use-pending-join-requests';

export function ProfileMenu() {
  const { user } = useSession();
  const { logout, isPending } = useLogout();
  const pending = usePendingJoinRequests();
  const { isOpen, containerRef, toggle, close } = useDisclosure<HTMLDivElement>();

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Menu akun dan pengaturan"
        className="relative inline-flex size-11 items-center justify-center rounded-field hover:bg-ink/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
      >
        <InitialAvatar name={user?.displayName ?? ''} className="size-8 text-sm" />
        {pending > 0 ? (
          <span
            aria-hidden="true"
            className="absolute top-1.5 right-1.5 size-2.5 rounded-full bg-brass ring-2 ring-card"
          />
        ) : null}
      </button>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            role="menu"
            aria-orientation="vertical"
            className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-card border border-rule bg-card py-1.5 shadow-lift"
          >
            <div className="border-b border-rule px-3.5 py-2">
              <p className="truncate text-sm font-medium text-ink">{user?.displayName}</p>
              {user?.username ? <p className="truncate text-xs text-muted">@{user.username}</p> : null}
            </div>

            <ul className="py-1">
              <li>
                <Link
                  to={ROUTES.settings}
                  onClick={close}
                  role="menuitem"
                  className="flex min-h-11 items-center gap-3 px-3.5 text-sm text-ink hover:bg-paper"
                >
                  <GearSix size={18} weight="duotone" className="shrink-0 text-muted" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">Pengaturan</span>
                  {pending > 0 ? (
                    <CountBadge
                      count={pending}
                      label={`${pending} permintaan bergabung`}
                      className="shrink-0"
                    />
                  ) : null}
                </Link>
              </li>
              <li>
                <Link
                  to={ROUTES.profile}
                  onClick={close}
                  role="menuitem"
                  className="flex min-h-11 items-center gap-3 px-3.5 text-sm text-ink hover:bg-paper"
                >
                  <User size={18} weight="duotone" className="shrink-0 text-muted" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">Profil & Pengingat</span>
                </Link>
              </li>
            </ul>

            <div className="border-t border-rule pt-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  void logout();
                }}
                disabled={isPending}
                className="flex min-h-11 w-full items-center gap-3 px-3.5 text-left text-sm text-muted hover:bg-paper hover:text-ink disabled:opacity-50"
              >
                <SignOut size={18} weight="duotone" className="shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">Keluar</span>
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
